import { getEndMs } from "./calendarHelpers";

/**
 * Detecção de colisão + layout em colunas para eventos sobrepostos, no estilo
 * Google Calendar (com expansão à direita).
 *
 * Algoritmo:
 *  1. Ordena por startTime (desempate por end).
 *  2. Agrupa em clusters: eventos conectados direta ou transitivamente por
 *     sobreposição (overlap = a.start < b.end && a.end > b.start).
 *  3. Greedy de colunas: cada evento recebe a menor coluna livre onde não
 *     colide com nenhum evento já alocado naquela coluna.
 *  4. EXPANSÃO À DIREITA: após fixar a coluna de cada evento, expande sua
 *     largura (`colSpan`) para ocupar as colunas contíguas à direita que
 *     estejam livres durante TODO o intervalo do evento — parando na primeira
 *     coluna onde houver colisão com um evento sobreposto. Isso elimina o
 *     desperdício de largura quando há espaço livre à direita.
 *
 * Cada evento retorna `{ event, column, colSpan, totalColumns }`. O consumidor
 * calcula:
 *   left  = (column  / totalColumns) * 100%
 *   width = (colSpan / totalColumns) * 100% - gap
 *
 * @param {Array<{ id: string, startTime: string }>} events
 * @returns {Array<{ event: object, column: number, colSpan: number, totalColumns: number }>}
 */
export function computeEventColumns(events) {
  if (!events || events.length === 0) return [];

  const items = events
    .map((event) => ({
      event,
      start: new Date(event.startTime).getTime(),
      end: getEndMs(event),
    }))
    .filter((it) => !Number.isNaN(it.start))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const result = [];
  let cluster = [];
  let clusterMaxEnd = -Infinity;

  const flushCluster = () => {
    if (cluster.length === 0) return;

    // Greedy: cada coluna mantém o end do último evento alocado.
    const columnsEnd = []; // index = coluna, valor = end do último evento
    const placed = cluster.map((it) => {
      let col = 0;
      while (col < columnsEnd.length && columnsEnd[col] > it.start) {
        col += 1;
      }
      columnsEnd[col] = it.end;
      return { ...it, column: col };
    });

    const totalColumns = columnsEnd.length;

    // Expansão à direita: para cada evento, conta quantas colunas contíguas à
    // direita ele pode ocupar sem colidir com outro evento do cluster que se
    // sobreponha no tempo. Verifica TODAS as colunas candidatas contra TODOS os
    // eventos sobrepostos (não só o "último" da coluna), garantindo o ótimo.
    placed.forEach((p) => {
      let colSpan = 1;
      for (let nextCol = p.column + 1; nextCol < totalColumns; nextCol += 1) {
        const collision = placed.some(
          (other) =>
            other !== p &&
            other.column === nextCol &&
            other.start < p.end &&
            other.end > p.start,
        );
        if (collision) break;
        colSpan += 1;
      }
      result.push({
        event: p.event,
        column: p.column,
        colSpan,
        totalColumns,
      });
    });

    cluster = [];
    clusterMaxEnd = -Infinity;
  };

  for (const it of items) {
    // Se o evento não sobrepõe o cluster atual, fecha o cluster.
    if (cluster.length > 0 && it.start >= clusterMaxEnd) {
      flushCluster();
    }
    cluster.push(it);
    clusterMaxEnd = Math.max(clusterMaxEnd, it.end);
  }
  flushCluster();

  return result;
}
